interface BreadcrumbItem {
  "@type": "ListItem";
  position: number;
  name: string;
  item: string;
}

interface FAQItem {
  "@type": "Question";
  name: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
}

interface StructuredDataInput {
  breadcrumbs?: BreadcrumbItem[];
  faqs?: FAQItem[];
}

interface StructuredDataProps {
  type: "organization" | "website" | "breadcrumb" | "product" | "faq";
  data?: StructuredDataInput;
}

/**
 * Generates structured data script tag for different schema types based on input parameters.
 * @example
 * generateStructuredData({ type: 'organization', data: {} })
 * // Returns structured data script tag for organization schema type
 * @param {Object} input - Input object containing type and optional data for structured data.
 * @param {string} input.type - The type of structured data schema to generate (e.g., 'organization', 'website', 'breadcrumb', 'product', 'faq').
 * @param {Object} [input.data] - Optional data input for specific schema types (used for 'breadcrumb' and 'faq').
 * @returns {JSX.Element|null} A script tag with structured data or null if type is not supported.
 */
export const StructuredData = ({ type: _type, data }: StructuredDataProps) => {
  /**
   * Returns structured data in JSON-LD format according to the specified type.
   * @example
   * structuredData('organization')
   * // Returns a JSON object with structured data for an organization
   * @param {string} type - The type of structured data to return ('organization', 'website', 'breadcrumb', 'product', or 'faq').
   * @param {Object} [data] - Optional parameter for additional data required for specific structured data types (e.g., breadcrumbs or FAQs).
   * @returns {Object|null} Returns a JSON object with structured data in JSON-LD format or null if the type is not recognized.
   **/
  const getStructuredData = () => {
    switch (_type) {
      case "organization":
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Tellerwand",
          url: "https://tellerwand.com",
          logo: "https://tellerwand-assets.blr1.cdn.digitaloceanspaces.com/brand/logo.png",
          description:
            "Tellerwand centralizes your finances—investments, expenses, and obligations—so you always know where you stand.",
          foundingDate: "2024",
          sameAs: ["https://twitter.com/tellerwand"],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            email: "support@tellerwand.com",
          },
        };

      case "website":
        return {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Tellerwand",
          url: "https://tellerwand.com",
          description: "Personal finance management platform",
          publisher: {
            "@type": "Organization",
            name: "Tellerwand",
          },
        };

      case "breadcrumb":
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: data?.breadcrumbs || [],
        };

      case "product":
        return {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "TellerWand",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web Browser",
          description:
            "TellerWand is a family wealth management and personal finance platform for Indian households.",
          url: "https://tellerwand.com",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
          },
          provider: {
            "@type": "Organization",
            name: "TellerWand",
          },
          featureList: [
            "Family Wealth Management",
            "Portfolio Tracking",
            "Expense Management",
            "Investment Analysis",
            "Financial Reports",
            "Goal Setting",
            "Wealth Dashboard",
          ],
        };

      case "faq":
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: data?.faqs || [
            {
              "@type": "Question",
              name: "What is TellerWand?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "TellerWand is a comprehensive personal finance management SaaS platform that helps you track investments, expenses, and savings in one place. It provides smart dashboards for managing mutual funds, stocks, PPF, EPF, and more.",
              },
            },
            {
              "@type": "Question",
              name: "How does TellerWand help with investment tracking?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "TellerWand provides portfolio tracking, investment analysis, and wealth dashboards to help you monitor your investments across different asset classes including mutual funds, stocks, and retirement accounts.",
              },
            },
          ],
        };

      default:
        return null;
    }
  };

  const structuredData = getStructuredData();

  if (!structuredData) return null;

  // JSON.stringify is safe here as it properly escapes all content
  // The structured data is generated from controlled inputs, not user content
  const jsonContent = JSON.stringify(structuredData, null, 0);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: jsonContent,
      }}
    />
  );
};

export default StructuredData;
